import axios, { AxiosError, AxiosResponse } from "axios";
import { BACKEND_API_URL, MPM_API_URL } from "config";
import { acquireAccessToken } from "authService";
import {
  CreateIntegrationRequestPayload,
  CreateLicenseRequestPayload,
  UpdateLicenseRequestPayload,
  MPMIntegration,
  MPMLicense,
  License,
} from "./types";

/**
 * ---------------------------------------------------------
 * Axios Instance — Conditional Bearer Token
 * ---------------------------------------------------------
 * Adds Bearer token only for:
 * - /integrations
 * - /integrations/associations/${licenseId}
 */
const apiClient = axios.create({
  baseURL: MPM_API_URL(),
  headers: { "Content-Type": "application/json" },
});

apiClient.interceptors.request.use(async (config) => {
  const url = config.url ?? "";
  const needsAuth =
    url.startsWith("/integrations") ||
    url.startsWith("/integrations/associations");

  if (needsAuth) {
    try {
      const token = await acquireAccessToken();
      if (token) {
        config.headers = config.headers || {};
        config.headers.Authorization = `Bearer ${token}`;
      }
    } catch (err) {
      console.error("Failed to attach access token:", err);
    }
  }
  return config;
});

/**
 * ---------------------------------------------------------
 * Integration Endpoints (with token)
 * ---------------------------------------------------------
 */

export const retrieveIntegrations = async (): Promise<MPMIntegration[]> => {
  try {
    const response = await apiClient.get(`/integrations`);
    return response.data;
  } catch (error: any) {
    if (error.response?.status === 404) return [];
    console.error("Error retrieving integrations:", error);
    throw error;
  }
};

export const createIntegration = async (
  integration: CreateIntegrationRequestPayload
): Promise<MPMIntegration> => {
  const response = await apiClient.post(`/integrations`, integration);
  return response.data;
};

export const updateIntegration = async (
  integration: MPMIntegration
): Promise<MPMIntegration> => {
  const response = await apiClient.put(
    `/integrations/${integration.id}`,
    integration
  );
  return response.data;
};

export const deleteIntegration = async (integrationId: string): Promise<void> => {
  await apiClient.delete(`/integrations/${integrationId}`);
};

/**
 * ---------------------------------------------------------
 * License Endpoints (partially protected)
 * ---------------------------------------------------------
 */

export const retrieveLicense = async (
  licenseId: string
): Promise<MPMLicense> => {
  const response = await apiClient.get(
    `/integrations/associations/${licenseId}`
  );
  return { ...response.data, id: response.data.id.toString() };
};

export const retrieveLicenses = async (
  integrationId: string
): Promise<MPMLicense[]> => {
  try {
    const response = await apiClient.get(
      `/integrations/${integrationId}/associations`
    );
    return response.data.map((l: MPMLicense) => ({
      ...l,
      id: l.id.toString(),
    }));
  } catch (error: unknown) {
    if (axios.isAxiosError(error)) {
      if (error.response?.status === 404) {
        return [];
      }
    }
    console.error("Error retrieving licenses:", error);
    throw error;
  }
};


export const retrieveAllLicenses = async (
  integrationIds: string[]
): Promise<{ integrationId: string; licenses: MPMLicense[] }[]> => {
  const licensesMap = await Promise.all(
    integrationIds.map(async (integrationId) => {
      const licenses = await retrieveLicenses(integrationId);
      return { integrationId, licenses };
    })
  );
  return licensesMap;
};

export const createLicense = async (
  integrationId: string,
  license: License
): Promise<MPMLicense> => {
  const payload: CreateLicenseRequestPayload = {
    integrationId,
    licenseType: license.licenseType,
    softwareLicenseCode: license.softwareLicenseCode,
    count: license.count,
    expireDate: license.expireDate,
    customData: license.customData,
  };

  const response = await apiClient.post(`/integrations/associations`, payload);
  return response.data;
};

export const updateLicense = async (
  licenseId: string,
  licenseData: UpdateLicenseRequestPayload
): Promise<MPMLicense> => {
  const response = await apiClient.put(
    `/integrations/associations/${licenseId}`,
    licenseData
  );
  return response.data;
};

export default apiClient;

